import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, HeartHandshake, Package, ShieldCheck, GraduationCap, Users,
  CalendarDays, Building2, Contact2, Palette, ScrollText, LogOut, Download, Loader2, RefreshCw,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MEDIA } from "@/lib/media";

const INKIND_STATUSES = ["New Lead", "Contacted", "Verified", "Pickup Scheduled", "Delivered", "Declined", "Completed"];
const GENERIC_STATUSES = ["New", "Reviewing", "Contacted", "Approved", "Waitlisted", "Declined", "Completed"];

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, area: "any" },
  { key: "donations", label: "Donations", icon: HeartHandshake, area: "donations", cols: ["created_at", "donor_name", "email", "amount", "campaign", "recurring", "payment_status"] },
  { key: "inkind_pledges", label: "In-Kind Pledges", icon: Package, area: "inkind", cols: ["created_at", "donor_name", "item_name", "quantity", "email", "status"], statusList: INKIND_STATUSES },
  { key: "veteran_applications", label: "Veterans", icon: ShieldCheck, area: "veterans", cols: ["created_at", "name", "email", "city", "state", "branch", "status"], statusList: GENERIC_STATUSES },
  { key: "trainer_applications", label: "Trainers", icon: GraduationCap, area: "trainers", cols: ["created_at", "name", "email", "track", "status"], statusList: GENERIC_STATUSES },
  { key: "volunteer_applications", label: "Volunteers", icon: Users, area: "volunteers", cols: ["created_at", "name", "email", "phone", "status"], statusList: GENERIC_STATUSES },
  { key: "event_registrations", label: "14DDV Regs", icon: CalendarDays, area: "events", cols: ["created_at", "name", "email", "category", "ticket_type", "status"], statusList: GENERIC_STATUSES },
  { key: "sponsor_inquiries", label: "Sponsors", icon: Building2, area: "sponsors", cols: ["created_at", "organization", "contact_name", "email", "tier", "status"], statusList: GENERIC_STATUSES },
  { key: "contacts", label: "CRM", icon: Contact2, area: "crm", cols: ["created_at", "name", "email", "phone", "tags"] },
  { key: "artists", label: "Artists", icon: Palette, area: "content", cols: ["created_at", "name", "email", "status"], statusList: GENERIC_STATUSES },
  { key: "email_logs", label: "Logs", icon: ScrollText, area: "super", cols: ["created_at", "recipient", "subject", "category", "status"], extra: true },
];

const fmt = (v, k) => {
  if (v === null || v === undefined || v === "") return "—";
  if (k === "amount") return `$${Number(v).toLocaleString()}`;
  if (k === "created_at") return new Date(v).toLocaleDateString();
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && !user) navigate("/admin/login", { replace: true }); }, [user, loading, navigate]);

  const loadOverview = useCallback(() => { api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user) loadOverview(); }, [user, loadOverview]);

  const active = TABS.find((t) => t.key === tab);

  const loadRows = useCallback(async (key) => {
    setBusy(true);
    try { const r = await api.get(`/admin/data/${key}`); setRows(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Access denied"); setRows([]); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { if (user && tab !== "overview" && tab !== "users") loadRows(tab); }, [tab, user, loadRows]);

  const updateStatus = async (resource, id, status) => {
    try {
      if (resource === "inkind_pledges") await api.patch(`/admin/inkind_pledges/${id}`, { status });
      else await api.patch(`/admin/${resource}/${id}/status`, { status });
      toast.success("Status updated");
      loadRows(resource); loadOverview();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const exportCsv = (resource) => window.open(`${API}/admin/export/${resource}`, "_blank");

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#C8C4B7]">Loading…</div>;

  const stats = overview ? [
    ["Donations", `$${overview.donations_total.toLocaleString()}`],
    ["Gifts", overview.donations_count],
    ["Recurring donors", overview.recurring_donors],
    ["In-kind pledges", overview.inkind_pledges],
    ["Veteran apps", overview.veteran_apps],
    ["Trainer apps", overview.trainer_apps],
    ["Volunteer apps", overview.volunteer_apps],
    ["14DDV regs", overview.event_regs],
    ["Sponsors", overview.sponsors],
    ["Contacts", overview.contacts],
  ] : [];

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar */}
      <aside className="w-60 bg-[#141414] border-r border-white/10 hidden md:flex flex-col shrink-0">
        <div className="p-5 flex items-center gap-3 border-b border-white/10">
          <img src={MEDIA.logo} alt="" className="w-10 h-10 object-contain" />
          <div><div className="font-heading text-[#FDFBF7] font-bold">Golden Wolf</div><div className="text-[10px] text-[#D4AF37] tracking-widest">ADMIN</div></div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${tab === t.key ? "text-[#D4AF37] bg-white/5 border-l-2 border-[#D4AF37]" : "text-[#C8C4B7] hover:text-[#FDFBF7] border-l-2 border-transparent"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
          {user.role === "Super Administrator" && (
            <button onClick={() => setTab("users")} data-testid="tab-users" className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${tab === "users" ? "text-[#D4AF37] bg-white/5 border-l-2 border-[#D4AF37]" : "text-[#C8C4B7] hover:text-[#FDFBF7] border-l-2 border-transparent"}`}>
              <Users className="w-4 h-4" /> Users & Roles
            </button>
          )}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-[#8A877B] mb-1">{user.email}</div>
          <div className="text-xs text-[#D4AF37] mb-3">{user.role}</div>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-btn" className="flex items-center gap-2 text-sm text-[#C8C4B7] hover:text-red-400 transition-colors"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-auto">
        {/* mobile tab bar */}
        <div className="md:hidden flex gap-1 overflow-x-auto bg-[#141414] border-b border-white/10 p-2">
          {TABS.map((t) => <button key={t.key} onClick={() => setTab(t.key)} className={`text-xs px-3 py-2 whitespace-nowrap ${tab === t.key ? "text-[#D4AF37]" : "text-[#C8C4B7]"}`}>{t.label}</button>)}
        </div>

        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="font-heading text-3xl font-bold text-[#FDFBF7]">{active?.label || "Users & Roles"}</h1>
            <div className="flex gap-2">
              {tab !== "overview" && tab !== "users" && <>
                <button onClick={() => loadRows(tab)} className="btn-outline text-sm flex items-center gap-1" data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /></button>
                <button onClick={() => exportCsv(tab)} className="btn-gold text-sm flex items-center gap-1" data-testid="export-btn"><Download className="w-4 h-4" /> CSV</button>
              </>}
            </div>
          </div>

          {tab === "overview" && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="overview-stats">
              {stats.map(([l, v]) => (
                <div key={l} className="gw-card p-5"><div className="text-xs text-[#8A877B] uppercase tracking-wider">{l}</div><div className="font-heading text-3xl font-bold text-[#D4AF37] mt-2">{v}</div></div>
              ))}
            </div>
          )}

          {tab === "users" && <UsersTab currentUser={user} />}

          {tab !== "overview" && tab !== "users" && (
            <div className="gw-card overflow-x-auto" data-testid="admin-table">
              {busy ? <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37] mx-auto" /></div> :
                rows.length === 0 ? <div className="p-12 text-center text-[#8A877B]">No records yet.</div> : (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-white/10 text-left">
                      {active.cols.map((c) => <th key={c} className="px-4 py-3 text-[#8A877B] font-medium uppercase text-xs tracking-wider">{c.replace(/_/g, " ")}</th>)}
                      {active.statusList && <th className="px-4 py-3 text-[#8A877B] font-medium uppercase text-xs tracking-wider">Update</th>}
                    </tr></thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.id || i} className="border-b border-white/5 hover:bg-white/5">
                          {active.cols.map((c) => <td key={c} className="px-4 py-3 text-[#C8C4B7] max-w-[220px] truncate">{fmt(r[c], c)}</td>)}
                          {active.statusList && (
                            <td className="px-4 py-3">
                              <select value={r.status || ""} data-testid={`status-select-${r.id}`} onChange={(e) => updateStatus(tab, r.id, e.target.value)}
                                className="bg-[#1C1C1C] border border-white/15 text-[#FDFBF7] text-xs px-2 py-1.5 outline-none focus:border-[#D4AF37]">
                                {active.statusList.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const ROLES = ["Super Administrator", "Donation Coordinator", "Veteran Program Coordinator", "Trainer Coordinator", "Volunteer Coordinator", "Event Manager", "Content Editor", "Finance Viewer"];

function UsersTab({ currentUser }) {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setRole = async (id, role) => { try { await api.patch(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); load(); } catch { toast.error("Failed"); } };
  return (
    <div className="gw-card overflow-x-auto" data-testid="users-table">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-white/10 text-left">{["Name", "Email", "Role"].map((h) => <th key={h} className="px-4 py-3 text-[#8A877B] uppercase text-xs tracking-wider">{h}</th>)}</tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} className="border-b border-white/5">
              <td className="px-4 py-3 text-[#FDFBF7]">{u.name}</td>
              <td className="px-4 py-3 text-[#C8C4B7]">{u.email}</td>
              <td className="px-4 py-3">
                <select value={u.role} disabled={u.user_id === currentUser.user_id} data-testid={`role-select-${u.user_id}`} onChange={(e) => setRole(u.user_id, e.target.value)}
                  className="bg-[#1C1C1C] border border-white/15 text-[#FDFBF7] text-xs px-2 py-1.5 outline-none focus:border-[#D4AF37] disabled:opacity-50">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
